import React, { useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "@tanstack/react-query";
import { useHabitContext } from "../../context/HabitContext";
import { useAuth } from "../../context/AuthContext";
import { queryKeys } from "../../lib/queryKeys";
import * as db from "../../services/dbService";
import { getDaysInMonth, getMonthShort } from "../../utils/dateUtils";
import SectionHeader from "../common/SectionHeader";
import Spinner from "../common/Spinner";
import "./TasksYearView.scss";

type CellTooltipPlacement = 'center' | 'start' | 'end';

const MONTHS = Array.from({ length: 12 }, (_, i) => i);
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

const TASK_COLORS = ['var(--bg-secondary)', '#bbf7d0', '#4ade80', '#16a34a', '#14532d'];

function getTaskCountColor(count: number): string {
  if (count === 0) return TASK_COLORS[0];
  if (count === 1) return TASK_COLORS[1];
  if (count <= 3) return TASK_COLORS[2];
  if (count <= 6) return TASK_COLORS[3];
  return TASK_COLORS[4];
}

function getTooltipPlacement(index: number, total: number): CellTooltipPlacement {
  const edgeThreshold = total > 20 ? 2 : 1;
  if (index < edgeThreshold) return 'start';
  if (index >= total - edgeThreshold) return 'end';
  return 'center';
}

const TasksYearView: React.FC = () => {
  const { formatMessage: t, locale } = useIntl();
  const { currentYear } = useHabitContext();
  const { userId } = useAuth();

  const [viewYear, setViewYear] = useState(currentYear);

  const now = new Date();
  const todayYear = now.getFullYear();
  const todayMonth = now.getMonth();
  const todayDay = now.getDate();

  const { data: allTodos = [], isLoading } = useQuery({
    queryKey: queryKeys.todos(userId, `year-${viewYear}`),
    queryFn: () => db.fetchTodos({ year: viewYear, includeDeleted: false }),
    staleTime: 5 * 60_000,
    enabled: !!userId,
  });

  // Build map of completed tasks keyed by "M-D"
  const completedByDate: Record<string, Array<{ id: string; title: string }>> = {};

  allTodos.forEach(todo => {
    if (!todo.completed || !todo.scheduledDate) return;
    const date = new Date(todo.scheduledDate);
    if (date.getFullYear() !== viewYear) return;
    const key = `${date.getMonth()}-${date.getDate()}`;
    if (!completedByDate[key]) completedByDate[key] = [];
    completedByDate[key].push({ id: todo.id, title: todo.title });
  });

  const isFutureCell = (m: number, day: number) =>
    viewYear > todayYear ||
    (viewYear === todayYear && m > todayMonth) ||
    (viewYear === todayYear && m === todayMonth && day > todayDay);

  const getCellDateLabel = (m: number, day: number) =>
    `${getMonthShort(m, locale)} ${day}, ${viewYear}`;

  const renderCellTooltip = (
    m: number,
    day: number,
    tasks: Array<{ id: string; title: string }>,
    placement: CellTooltipPlacement,
  ) => {
    if (tasks.length === 0) return null;
    return (
      <div className={`hyd__cell-tooltip hyd__cell-tooltip--${placement}`} role="tooltip">
        <div className="hyd__cell-tooltip-date">{getCellDateLabel(m, day)}</div>
        <div className="hyd__cell-tooltip-items">
          {tasks.map(task => (
            <div key={task.id} className="hyd__cell-tooltip-item hyd__cell-tooltip-item--done">
              <span className="hyd__cell-tooltip-dot" style={{ background: '#4ade80' }} />
              <span className="hyd__cell-tooltip-name">{task.title}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCell = (m: number, day: number, tooltipIndexInRow: number, rowLength: number) => {
    const daysInMonth = getDaysInMonth(m, viewYear);
    if (day > daysInMonth) {
      return <td key={day} className="hyd__cell hyd__cell--void" />;
    }
    if (isFutureCell(m, day)) {
      return (
        <td key={day} className="hyd__cell">
          <div className="hyd__cell-color" style={{ background: 'var(--bg-secondary)', opacity: 0.35 }} />
        </td>
      );
    }
    const key = `${m}-${day}`;
    const tasks = completedByDate[key] ?? [];
    const color = getTaskCountColor(tasks.length);
    const placement = getTooltipPlacement(tooltipIndexInRow, rowLength);
    return (
      <td
        key={day}
        className="hyd__cell"
        aria-label={tasks.length > 0 ? `${getCellDateLabel(m, day)}: ${tasks.length} completed` : undefined}
      >
        <div className="hyd__cell-color" style={{ background: color }} />
        {tasks.length > 0 && renderCellTooltip(m, day, tasks, placement)}
      </td>
    );
  };

  const totalCompleted = Object.values(completedByDate).reduce((sum, tasks) => sum + tasks.length, 0);

  return (
    <div className="tasks-year-view">
      <SectionHeader
        title={String(viewYear)}
        onPrev={() => setViewYear(y => y - 1)}
        onNext={() => setViewYear(y => y + 1)}
        prevLabel={t({ id: 'stats.tasks.previousYear' })}
        nextLabel={t({ id: 'stats.tasks.nextYear' })}
      />

      {isLoading ? (
        <Spinner block />
      ) : totalCompleted === 0 ? (
        <p className="tasks-year-view__empty">
          {t({ id: 'year.tasks.noTasks' })}
        </p>
      ) : (
        <div className="tasks-year-view__grid">
          {/* Desktop: months as rows, days 1-31 as columns */}
          <table className="hyd__table hyd__table--desktop">
            <thead>
              <tr>
                <th className="hyd__corner" />
                {DAYS.map(day => (
                  <th key={day} className="hyd__day-head">{day}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MONTHS.map(m => (
                <tr key={m} className="hyd__month-row">
                  <td className="hyd__month-label">{getMonthShort(m, locale)}</td>
                  {DAYS.map((day, idx) => renderCell(m, day, idx, DAYS.length))}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile: days as rows, months as columns */}
          <table className="hyd__table hyd__table--mobile">
            <thead>
              <tr>
                <th className="hyd__corner" />
                {MONTHS.map(m => (
                  <th key={m} className="hyd__month-head">{getMonthShort(m, locale)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DAYS.map(day => (
                <tr key={day} className="hyd__day-row">
                  <td className="hyd__day-num">{day}</td>
                  {MONTHS.map((m, idx) => renderCell(m, day, idx, MONTHS.length))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TasksYearView;